export type AdminAnalyticsTabValue =
  | 'overview'
  | 'devices'
  | 'screens'
  | 'flows'
  | 'users'
  | 'quality'
  | 'performance';

export type AdminAnalyticsPresetValue = '30d' | '90d' | 'custom';

type AdminAnalyticsUrlStateInput = {
  tab: AdminAnalyticsTabValue;
  preset: AdminAnalyticsPresetValue;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  organizationIds: string[];
  cohortIds: string[];
  flowIds: string[];
  includeStaff: boolean;
  userSearch: string;
};

export type AdminAnalyticsUrlState = AdminAnalyticsUrlStateInput & {
  filterStateKey: string;
  stateKey: string;
};

export const ADMIN_ANALYTICS_DEFAULT_TAB: AdminAnalyticsTabValue = 'overview';
export const ADMIN_ANALYTICS_DEFAULT_PRESET: AdminAnalyticsPresetValue = '30d';
export const ADMIN_ANALYTICS_DEFAULT_USER_SORT = 'user_asc';

const ADMIN_ANALYTICS_TAB_VALUES: AdminAnalyticsTabValue[] = [
  'overview',
  'devices',
  'screens',
  'flows',
  'users',
  'quality',
  'performance',
];

const ADMIN_ANALYTICS_PRESET_VALUES: AdminAnalyticsPresetValue[] = ['30d', '90d', 'custom'];

const QUERY_PARAM_KEYS = {
  tab: 'tab',
  range: 'range',
  startDate: 'startDate',
  endDate: 'endDate',
  organizationIds: 'orgs',
  cohortIds: 'cohorts',
  flowIds: 'flows',
  includeStaff: 'staff',
  userSearch: 'userSearch',
} as const;

const buildDateRange = (days: number) => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - (days - 1));
  return {
    startDate: startDate.toISOString().slice(0, 10),
    endDate: endDate.toISOString().slice(0, 10),
  };
};

export const getAdminAnalyticsPresetRange = (preset: AdminAnalyticsPresetValue) =>
  preset === '90d' ? buildDateRange(90) : buildDateRange(30);

const isIsoDate = (value: string | null) => Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value));

const parseDelimitedIds = (value: string | null) => {
  if (!value) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean),
    ),
  );
};

const buildDelimitedIds = (value: string[]) => value.join(',');

const buildFilterStateKey = (state: Omit<AdminAnalyticsUrlStateInput, 'tab'>) =>
  JSON.stringify([
    state.preset,
    state.dateRange.startDate,
    state.dateRange.endDate,
    state.organizationIds,
    state.cohortIds,
    state.includeStaff,
    state.userSearch,
  ]);

const buildStateKey = (state: AdminAnalyticsUrlStateInput) =>
  JSON.stringify([state.tab, buildFilterStateKey(state), state.flowIds]);

export const parseAdminAnalyticsSearchParams = (searchParams: URLSearchParams): AdminAnalyticsUrlState => {
  const rawTab = searchParams.get(QUERY_PARAM_KEYS.tab);
  const tab = ADMIN_ANALYTICS_TAB_VALUES.includes(rawTab as AdminAnalyticsTabValue)
    ? (rawTab as AdminAnalyticsTabValue)
    : ADMIN_ANALYTICS_DEFAULT_TAB;

  const rawPreset = searchParams.get(QUERY_PARAM_KEYS.range);
  const preset = ADMIN_ANALYTICS_PRESET_VALUES.includes(rawPreset as AdminAnalyticsPresetValue)
    ? (rawPreset as AdminAnalyticsPresetValue)
    : ADMIN_ANALYTICS_DEFAULT_PRESET;

  const dateRange =
    preset === 'custom' &&
    isIsoDate(searchParams.get(QUERY_PARAM_KEYS.startDate)) &&
    isIsoDate(searchParams.get(QUERY_PARAM_KEYS.endDate))
      ? {
          startDate: searchParams.get(QUERY_PARAM_KEYS.startDate) as string,
          endDate: searchParams.get(QUERY_PARAM_KEYS.endDate) as string,
        }
      : getAdminAnalyticsPresetRange(preset);

  const state: AdminAnalyticsUrlStateInput = {
    tab,
    preset,
    dateRange,
    organizationIds: parseDelimitedIds(searchParams.get(QUERY_PARAM_KEYS.organizationIds)),
    cohortIds: parseDelimitedIds(searchParams.get(QUERY_PARAM_KEYS.cohortIds)),
    flowIds: parseDelimitedIds(searchParams.get(QUERY_PARAM_KEYS.flowIds)),
    includeStaff: ['1', 'true'].includes((searchParams.get(QUERY_PARAM_KEYS.includeStaff) ?? '').trim()),
    userSearch: (searchParams.get(QUERY_PARAM_KEYS.userSearch) ?? '').trim(),
  };

  return {
    ...state,
    filterStateKey: buildFilterStateKey(state),
    stateKey: buildStateKey(state),
  };
};

export const buildAdminAnalyticsSearchParams = (
  searchParams: URLSearchParams,
  state: AdminAnalyticsUrlStateInput,
) => {
  const nextParams = new URLSearchParams(searchParams);
  Object.values(QUERY_PARAM_KEYS).forEach((key) => nextParams.delete(key));

  if (state.tab !== ADMIN_ANALYTICS_DEFAULT_TAB) {
    nextParams.set(QUERY_PARAM_KEYS.tab, state.tab);
  }

  if (state.preset !== ADMIN_ANALYTICS_DEFAULT_PRESET) {
    nextParams.set(QUERY_PARAM_KEYS.range, state.preset);
  }

  if (state.preset === 'custom') {
    nextParams.set(QUERY_PARAM_KEYS.startDate, state.dateRange.startDate);
    nextParams.set(QUERY_PARAM_KEYS.endDate, state.dateRange.endDate);
  }

  if (state.organizationIds.length) {
    nextParams.set(QUERY_PARAM_KEYS.organizationIds, buildDelimitedIds(state.organizationIds));
  }

  if (state.cohortIds.length) {
    nextParams.set(QUERY_PARAM_KEYS.cohortIds, buildDelimitedIds(state.cohortIds));
  }

  if (state.flowIds.length) {
    nextParams.set(QUERY_PARAM_KEYS.flowIds, buildDelimitedIds(state.flowIds));
  }

  if (state.includeStaff) {
    nextParams.set(QUERY_PARAM_KEYS.includeStaff, '1');
  }

  if (state.userSearch.trim()) {
    nextParams.set(QUERY_PARAM_KEYS.userSearch, state.userSearch.trim());
  }

  return nextParams;
};
